/**
 * The EVERYONE half of the Memorial Wall (WALL-contracts): the dead other players chose to put up.
 *
 * It is deliberately undemanding. The wall is read once per visit and cached for the screen's life;
 * a server that says nothing is not an error the player has to deal with, it just means his own
 * dead are what is here. Nothing on this side can be edited, reported or replied to - it is a wall
 * of names, and the only control anyone has over it is whether his own names went up at all.
 */

import type { Ctx } from './context';
import { el } from './dom';
import { plaque } from './memorial';
import { publicPicture } from './photoView';
import type { PublicName, WallPort } from './wallPort';

/** How a name is dated on a wall that spans everybody: the day, never a clock. */
export function wallDate(at: number, now: number): string {
  const days = Math.floor((now - at) / 86_400_000);
  if (days <= 0) return 'TODAY';
  if (days === 1) return 'YESTERDAY';
  if (days < 7) return `${days} DAYS AGO`;
  if (days < 365) return `${Math.floor(days / 7)} WEEK${days < 14 ? '' : 'S'} AGO`;
  return 'OVER A YEAR AGO';
}

export interface PublicWallView {
  show(): Promise<void>;
}

/**
 * @param grid the card grid both walls draw into
 * @param count the header's name count
 */
export function publicWallView(
  ctx: Ctx,
  port: WallPort | null,
  grid: HTMLElement,
  count: HTMLElement,
): PublicWallView {
  /** Read once per visit: walking back and forth between the two walls must not hammer the server. */
  let loaded: { names: PublicName[]; total: number; mine: string | null } | null = null;
  let reading: Promise<void> | null = null;

  const say = (text: string): void => {
    grid.replaceChildren(el('div', 'menu-empty menu-prose', text));
  };

  const paint = (): void => {
    if (loaded === null) return;
    const { names, total, mine } = loaded;
    count.textContent = `${total} NAME${total === 1 ? '' : 'S'} FROM EVERY COMPANY`;
    if (names.length === 0) {
      say('NO NAMES HAVE BEEN PUT UP YET. YOURS COULD BE THE FIRST - AND NEED NOT BE.');
      return;
    }
    const now = Date.now();
    grid.replaceChildren(...names.map((name) => {
      // PHOTOS: a picture only where the name went up with one, from the wall's address - never by the
      // entry's own photo id, which belongs to whichever save put him there.
      const card = plaque(ctx, name.entry, mine !== null && name.by === mine, publicPicture(ctx, name));
      card.append(el('div', 'menu-plaque-line menu-small', wallDate(name.at, now)));
      return card;
    }));
  };

  return {
    async show() {
      if (port === null) {
        say('THERE IS NO PUBLIC WALL HERE.');
        return;
      }
      if (loaded !== null) {
        paint();
        return;
      }
      count.textContent = '';
      say('READING THE WALL...');
      // A second visit while the first read is still out waits on it rather than starting another.
      reading ??= (async () => {
        const result = await port.read();
        if (result.kind === 'ok') loaded = { names: result.names, total: result.count, mine: result.mine };
        reading = null;
      })();
      await reading;
      if (loaded === null) {
        count.textContent = '';
        say('THE WALL CANNOT BE REACHED. YOUR OWN DEAD ARE STILL HERE.');
        return;
      }
      paint();
    },
  };
}
